/* eslint-disable */
/**
 * Triage UI label/color mappings.
 * Shared by LeadsTable and LeadDetail.
 */

// Reason code → human-readable chip label
export const REASON_LABELS = {
    HIGH_PROFIT_PCT: 'High profit',
    HIGH_SPREAD: 'Big discount',
    LARGE_ARV_GAP: 'Big ARV gap',
    DISTRESS_PRESENT: 'Distress',
    MULTI_SOURCE_AVM: 'Strong data',
    COMPLETE_DEAL_DATA: 'Strong data',
    HAS_CONTACT_INFO: 'Contactable',
    HAS_BUYER_MATCHES: 'Buyer matches',
    MATCHES_BUY_BOX: 'Matches buy box',
    PORTFOLIO_DEAL: 'Portfolio',
    MOTIVATED_SELLER: 'Motivated seller',
};

// Reason-code display priority (highest to lowest importance).
export const REASON_PRIORITY = [
    'HIGH_PROFIT_PCT',
    'HIGH_SPREAD',
    'LARGE_ARV_GAP',
    'DISTRESS_PRESENT',
    'HAS_BUYER_MATCHES',
    'MATCHES_BUY_BOX',
    'HAS_CONTACT_INFO',
    'COMPLETE_DEAL_DATA',
    'MULTI_SOURCE_AVM',
    'MOTIVATED_SELLER',
    'PORTFOLIO_DEAL',
];

/**
 * Deduplicate reason codes that map to the same label.
 * Returns { visible: [{code, label}], overflow: number }
 */
export function getUniqueReasonChips(reasonCodes, maxChips = 3) {
    const priorityMap = new Map(REASON_PRIORITY.map((code, idx) => [code, idx]));
    const rankedReasonCodes = (reasonCodes || [])
        .map((code, originalIndex) => ({ code, originalIndex }))
        .sort((a, b) => {
            const aPriority = priorityMap.has(a.code) ? priorityMap.get(a.code) : Number.MAX_SAFE_INTEGER;
            const bPriority = priorityMap.has(b.code) ? priorityMap.get(b.code) : Number.MAX_SAFE_INTEGER;
            if (aPriority !== bPriority) return aPriority - bPriority;
            return a.originalIndex - b.originalIndex;
        })
        .map(item => item.code);

    const seen = new Set();
    const chips = [];
    for (const code of rankedReasonCodes) {
        const label = REASON_LABELS[code];
        if (label && !seen.has(label)) {
            seen.add(label);
            chips.push({ code, label });
        }
    }
    const overflow = Math.max(0, chips.length - maxChips);
    return { visible: chips.slice(0, maxChips), overflow };
}

// Red flag → human-readable label (priority order)
export const RED_FLAG_LABELS = {
    NEGATIVE_PROFIT: 'Negative profit',
    PROFIT_SPREAD_CONFLICT: 'Profit/spread conflict',
    NO_CONTACT: 'No contact info',
    MISSING_BASICS: 'Missing property basics',
    NO_ESTIMATES: 'No value estimates',
};

export const RED_FLAG_PRIORITY = [
    'NEGATIVE_PROFIT', 'PROFIT_SPREAD_CONFLICT', 'NO_CONTACT',
    'MISSING_BASICS', 'NO_ESTIMATES',
];

export function getTopRedFlags(redFlags, max = 2) {
    return RED_FLAG_PRIORITY
        .filter(f => (redFlags || []).includes(f))
        .slice(0, max)
        .map(f => ({ code: f, label: RED_FLAG_LABELS[f] }));
}

// next_action.type → CTA button label
export const CTA_LABELS = {
    VERIFY_AND_DECIDE: 'Review',
    RUN_UNDERWRITING: 'Run comps',
    RUN_COMPS: 'Run comps',
    GET_ARV: 'Run comps',
    ESTIMATE_REPAIRS: 'Estimate repairs',
    GET_ASKING_PRICE: 'Request price',
    VERIFY_PROPERTY: 'Verify property',
    CLARIFY_PORTFOLIO: 'Clarify portfolio',
};

// Bucket → friendly label (for tooltips / headers)
export const BUCKET_LABELS = {
    UNDERWRITTEN: 'Underwritten',
    SPREAD_READY: 'Discount check',
    ARV_GAP_READY: 'ARV known, repairs missing',
    REPAIR_ONLY: 'Repairs known, ARV missing',
    PRICE_MISSING: 'Needs price',
    BASICS_MISSING: 'Needs address/basics',
    VALUE_MISSING: 'Needs value signal',
    PORTFOLIO_UNCLEAR: 'Needs property list',
};

// Tier → { label, bgClass, dotColor }
export const TIER_CONFIG = {
    LOOK_NOW:    { label: '🔥 Hot deal', bgClass: 'bg-colour-jade-01', dotColor: '#63EEA8' },
    LOOK_SOON:   { label: 'Review soon', bgClass: 'bg-colour-yellow-01', dotColor: '#F1C63C' },
    UNBLOCK_NOW: { label: 'Get info', bgClass: 'bg-colour-orange-01', dotColor: '#FD8F54' },
    LATER:       { label: 'Later', bgClass: 'bg-colour-light', dotColor: '#A9A7B4' },
};

// Triage tab definitions
export const TRIAGE_TABS = [
    { key: 'review', label: 'Review now', tiers: ['LOOK_NOW', 'LOOK_SOON'] },
    { key: 'getinfo', label: 'Get info', tiers: ['UNBLOCK_NOW'] },
    { key: 'later', label: 'Later', tiers: ['LATER'] },
    { key: 'all', label: 'All', tiers: null },
];
